import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const STORAGE_KEY = '@porsche/cart';

const useCartStore = create(
  persist(
    (set, get) => ({
      // State
      items: [],

      // Actions
      addItem: (item) => {
        if (!item || !item._id) return;

        const { items } = get();
        const existingItem = items.find((c) => c._id === item._id);

        if (existingItem) {
          // Update quantity and delivery info if item exists
          const updatedItems = items.map((c) =>
            c._id === item._id
              ? {
                  ...c,
                  cartQuantity: (c.cartQuantity || 0) + (item.cartQuantity || 1),
                  // Update delivery info from new inquiry
                  confirmedDeliveryDate: item.confirmedDeliveryDate || c.confirmedDeliveryDate,
                  selectedDeliveryDate: item.selectedDeliveryDate || c.selectedDeliveryDate,
                  scheduleDetails: item.scheduleDetails || c.scheduleDetails,
                  isNotInStock: item.isNotInStock ?? c.isNotInStock,
                  notInStockQuantity: item.notInStockQuantity ?? c.notInStockQuantity,
                }
              : c
          );
          set({ items: updatedItems });
        } else {
          // Add new item
          set({ items: [...items, item] });
        }
      },

      removeItem: (id) => {
        set((state) => ({
          items: state.items.filter((item) => item._id !== id),
        }));
      },

      updateItemQuantity: (id, quantity) => {
        set((state) => ({
          items: state.items.map((item) =>
            item._id === id ? { ...item, cartQuantity: quantity } : item
          ),
        }));
      },

      updateItem: (id, updates) => {
        set((state) => ({
          items: state.items.map((item) =>
            item._id === id ? { ...item, ...updates } : item
          ),
        }));
      },

      setItems: (items) => {
        set({ items });
      },

      toggleItemSelection: (id) => {
        set((state) => ({
          items: state.items.map((item) =>
            item._id === id ? { ...item, selected: !item.selected } : item
          ),
        }));
      },

      selectAll: () => {
        set((state) => ({
          items: state.items.map((item) => ({ ...item, selected: true })),
        }));
      },

      deselectAll: () => {
        set((state) => ({
          items: state.items.map((item) => ({ ...item, selected: false })),
        }));
      },

      removeSelected: () => {
        set((state) => ({
          items: state.items.filter((item) => !item.selected),
        }));
      },

      clear: () => {
        set({ items: [] });
      },

      // Computed values
      getItemCount: () => {
        const { items } = get();
        return items.reduce((sum, item) => sum + (item.cartQuantity || 0), 0);
      },

      getTotal: () => {
        const { items } = get();
        return items
          .reduce(
            (sum, item) =>
              sum + (item.grossPrice || 0) * (item.cartQuantity || 0),
            0
          )
          .toFixed(2);
      },

      hasSelectedItems: () => {
        return get().items.some((item) => item.selected);
      },

      allSelected: () => {
        const { items } = get();
        return items.length > 0 && items.every((item) => item.selected);
      },
    }),
    {
      name: STORAGE_KEY,
    }
  )
);

export default useCartStore;




